import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { IconMenu, IconX, IconChevronDown, IconChevronRight, IconSearch } from "./Icons";
import SearchOverlay from "./SearchOverlay";

// Only a short preview of brands lives in the nav — the rest live on /brands
const NAV_BRAND_LIMIT = 5;

const Navbar = ({ brandOptions, loadingBrandOptions }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const [mobileBrandOpen, setMobileBrandOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const brandRef = useRef(null);
  const navigate = useNavigate();

  const previewBrands = (brandOptions || []).slice(0, NAV_BRAND_LIMIT);
  const hasMoreBrands = (brandOptions || []).length > NAV_BRAND_LIMIT;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (brandRef.current && !brandRef.current.contains(e.target))
        setBrandOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen || searchOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen, searchOpen]);

  const handleBrandClick = (brand) => {
    setBrandOpen(false);
    setMobileOpen(false);
    navigate(`/watches?brand=${encodeURIComponent(brand.name)}`);
  };

  const closeAllMenus = () => {
    setBrandOpen(false);
    setMobileOpen(false);
  };

  return (
    <>
      <div className="announce-bar">
        Welcome to Legacy Timepiece — Visit our store at Plaza Blok M, 2nd
        Floor, Unit 2.24-2.25
      </div>

      <nav className={`nav ${scrolled ? "scrolled" : ""}`}>
        <div className="nav-inner">
          <Link to="/" className="nav-logo" style={{ textDecoration: "none" }}>
            Legacy Timepieces
            <span style={{ marginTop: "2px" }}>Est. 2015</span>
          </Link>
          <div className="nav-links">
            <Link
              to="/"
              className="nav-link"
              style={{ textDecoration: "none" }}
            >
              Home
            </Link>
            <div ref={brandRef} style={{ position: "relative" }}>
              <button
                className="nav-link"
                onClick={(e) => {
                  e.stopPropagation();
                  setBrandOpen(!brandOpen);
                }}
              >
                Brands <IconChevronDown />
              </button>
              {brandOpen && (
                <div className="brand-dropdown">
                  {loadingBrandOptions ? (
                    <div className="spinner-wrapper">
                      <div className="spinner spinner--sm" />
                    </div>
                  ) : (
                    <>
                      <div className="brand-dropdown-list">
                        {previewBrands.map((brand, index) => (
                          <button
                            key={index}
                            className="brand-btn"
                            onClick={() => handleBrandClick(brand)}
                          >
                            {brand.name}
                          </button>
                        ))}
                      </div>
                      <Link
                        to="/brands"
                        className="brand-dropdown-all"
                        onClick={closeAllMenus}
                      >
                        <span>
                          View All{hasMoreBrands ? ` ${brandOptions.length}` : ""}{" "}
                          Brands
                        </span>
                        <IconChevronRight />
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
            <Link
              to="/watches"
              className="nav-link"
              style={{ textDecoration: "none" }}
            >
              All Watches
            </Link>
            <Link
              to="/services"
              className="nav-link"
              style={{ textDecoration: "none" }}
            >
              Our Services
            </Link>
            <Link
              to="/about"
              className="nav-link"
              style={{ textDecoration: "none" }}
            >
              About
            </Link>
            <Link
              to="/#contact"
              className="nav-link"
              style={{ textDecoration: "none" }}
            >
              Contact
            </Link>
          </div>
          <button className="nav-search" onClick={() => setSearchOpen(true)}>
            <IconSearch /> Search
          </button>
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileOpen(true)}
          >
            <IconMenu />
          </button>
        </div>
      </nav>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <>
          <div
            className="mobile-overlay"
            onClick={() => setMobileOpen(false)}
          />
          <div className="mobile-drawer">
            <div className="mobile-drawer-header">
              <div className="nav-logo" style={{ fontSize: 20 }}>
                Legacy Timepieces<span style={{ fontSize: 8 }}>Est. 2015</span>
              </div>
              <button
                className="mobile-drawer-close"
                onClick={() => setMobileOpen(false)}
              >
                <IconX />
              </button>
            </div>
            <div className="mobile-nav-items">
              <button
                className="mobile-nav-item"
                onClick={() => {
                  setSearchOpen(true);
                  setMobileOpen(false);
                }}
              >
                Search
              </button>
              <Link
                to="/"
                className="mobile-nav-item"
                style={{ textDecoration: "none" }}
                onClick={() => setMobileOpen(false)}
              >
                Home
              </Link>
              <button
                className="mobile-nav-item"
                onClick={() => setMobileBrandOpen(!mobileBrandOpen)}
              >
                Brands {mobileBrandOpen ? "−" : "+"}
              </button>
              {mobileBrandOpen && (
                <div className="mobile-brand-list">
                  {loadingBrandOptions ? (
                    <div className="spinner-wrapper">
                      <div className="spinner spinner--sm" />
                    </div>
                  ) : (
                    <>
                      {previewBrands.map((brand, index) => (
                        <div
                          key={index}
                          className="mobile-brand-item"
                          onClick={() => handleBrandClick(brand)}
                        >
                          {brand.name}
                        </div>
                      ))}
                      <Link
                        to="/brands"
                        className="mobile-brand-all"
                        style={{ textDecoration: "none" }}
                        onClick={closeAllMenus}
                      >
                        <span>
                          View All{hasMoreBrands ? ` ${brandOptions.length}` : ""}{" "}
                          Brands
                        </span>
                        <IconChevronRight />
                      </Link>
                    </>
                  )}
                </div>
              )}
              <Link
                to="/watches"
                className="mobile-nav-item"
                style={{ textDecoration: "none" }}
                onClick={() => setMobileOpen(false)}
              >
                All Watches
              </Link>
              <Link
                to="/services"
                className="mobile-nav-item"
                style={{ textDecoration: "none" }}
                onClick={() => setMobileOpen(false)}
              >
                Our Services
              </Link>
              <Link
                to="/about"
                className="mobile-nav-item"
                style={{ textDecoration: "none" }}
                onClick={() => setMobileOpen(false)}
              >
                About
              </Link>
              <Link
                to="/#contact"
                className="mobile-nav-item"
                style={{ textDecoration: "none" }}
                onClick={() => setMobileOpen(false)}
              >
                Contact
              </Link>
            </div>
          </div>
        </>
      )}

      {/* Search Overlay */}
      {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} />}
    </>
  );
};

export default Navbar;
